import { Link, SelectInput, TextInput, Tile } from '@Components/common';
import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import styles from './LegalDetails.module.scss';
import { FormControl, FormHelperText, InputLabel } from '@material-ui/core';
import { createMenuItems, CreateMenuItemsProps } from '@Components/MenuItems/MenuItems';
import { OrganizationStructureEnum, UserTypeEnum } from '@Types';
import { RiLockFill } from 'react-icons/ri';
import { HelpCenterRoutes, RoutingPaths } from '@App/paths';
import { RegisteredDetails } from '@Types/OrganizationDueDiligenceData';
import { CompanyAndCharityNumberFields } from './Components/CompanyAndCharityNumberFields';
import { useUserContext } from '@Contexts';

export const LegalDetails = ({ typeOfOrganization }: { typeOfOrganization?: string }) => {
  const { t } = useTranslation('account');
  const { register, errors, control, watch } = useFormContext();
  const { userData } = useUserContext();
  const isFunder = userData?.user_type === UserTypeEnum.FUNDER;

  const fieldValue = (fieldName: keyof RegisteredDetails) => {
    const value = watch(fieldName);
    return value || '';
  };

  const organizationStructureOptions: CreateMenuItemsProps['items'] = useMemo(
    () =>
      Object.keys(OrganizationStructureEnum).map((key) => ({
        label: t(`tabs.organization.registered_details.legal_structure_options.${key}`),
        menuItemProps: {
          value: key,
        },
      })),
    [t],
  );

  return (
    <Tile
      title={{ title: t('tabs.organization.registered_details.legal_title') }}
      className={styles.container}
    >
      <div className={styles.container}>
        <div className={styles.sideBySideInputContainer}>
          <TextInput
            label={t('tabs.organization.registered_details.legal_name')}
            inputRef={register}
            name="legal_name"
            error={!!errors.legal_name}
            helperText={errors.legal_name?.message}
            InputLabelProps={{ shrink: fieldValue('legal_name') }}
          />
          <TextInput
            label={t('tabs.organization.registered_details.set_up_year')}
            type="number"
            inputRef={register({ valueAsNumber: true })}
            name="set_up_year"
            error={!!errors.set_up_year}
            helperText={errors.set_up_year?.message}
            InputLabelProps={{ shrink: fieldValue('set_up_year') }}
          />
        </div>
        <div className={styles.sideBySideInputContainer}>
          <TextInput
            label={t('tabs.organization.registered_details.type_of_organization')}
            name="type_of_organization"
            disabled={true}
            defaultValue={t(`organization_types_short_labels.${typeOfOrganization}`)}
            helperText={
              <Trans
                ns="account"
                i18nKey="tabs.organization.registered_details.type_of_organization_help"
                components={[<Link href={RoutingPaths.CONTACT} underline />]}
              />
            }
            InputProps={{
              startAdornment: <RiLockFill />,
            }}
          />
          {!isFunder && (
            <FormControl error={!!errors.legal_structure}>
              <InputLabel>{t('tabs.organization.registered_details.legal_structure')}</InputLabel>
              <Controller
                name="legal_structure"
                control={control}
                inputRef={register}
                defaultValue={''}
                render={({ ref, onChange, value }) => (
                  <SelectInput
                    inputRef={ref}
                    onChange={onChange}
                    label={t('tabs.organization.registered_details.legal_structure')}
                    value={value || ''}
                    error={!!errors.legal_structure}
                  >
                    {createMenuItems({ items: organizationStructureOptions })}
                  </SelectInput>
                )}
              />

              <FormHelperText>
                {errors.legal_structure?.message ? (
                  errors.legal_structure?.message
                ) : (
                  <Trans
                    ns="account"
                    i18nKey="tabs.organization.registered_details.legal_structure_help"
                    components={[<Link href={HelpCenterRoutes.LEGAL_STRUCTURE} underline />]}
                  />
                )}
              </FormHelperText>
            </FormControl>
          )}
        </div>
        {typeOfOrganization && (
          <CompanyAndCharityNumberFields typeOfOrganization={typeOfOrganization} />
        )}
      </div>
    </Tile>
  );
};
